import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Dropdown from 'UIKit/Dropdown'
import Avatar from 'UIKit/Image/Avatar'
import Link from 'UIKit/Link'
import NextLinkFromReactRouter from 'UIKit/NextLink'
import Skeleton from 'UIKit/Skeleton'
import Text from 'UIKit/Text'
import { RouteConfig } from 'config/constants/route'
import { ActionQueryEnum } from 'hooks/useActionQueryListener'
import { useCrisp, useElementContainerChatboxRendered, useIconChatHandle } from 'hooks/useCrisp'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import { useRouter } from 'hooks/useRouter'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { Trans } from 'react-i18next'
import { useCloseMenu, useMenuContainer } from 'state/app/hooks'
import { useAuth, useUserInfo } from 'state/auth/hooks'
import { useUnwatchedNewBonusListener, useUserBonus } from 'state/bonus/hooks'
import { useUnreadNotificationAmount } from 'state/notification/hooks'
import styled, { css } from 'styled-components'
import { Icons, getIcon } from 'svgs'
import { getActionUrlSearch } from 'utils/urlHelper'
import { AutoColumn } from './Column'
import { RowCenter } from './Row'
import UserMenuDropdown from './UserMenu/UserMenuDropdown'

const StyledWrapper = styled(AutoColumn).attrs({ gap: 'lg', justify: 'center' })`
  grid-template-columns: repeat(4, 1fr);

  position: fixed;
  left: 0px;
  bottom: 0px;
  background: ${({ theme }) => theme.colors.inputDefault};

  width: 100%;
  align-items: center;
  box-sizing: border-box;
  padding: 0 8px;
  z-index: 10;

  a {
    width: 100%;
  }

  ${({ theme }) => theme.mediaQueries.md} {
    display: none !important;
  }
`

const StyledText = styled(Text)<{ $active?: boolean }>`
  margin-top: auto;
  font-size: 12px;
  color: ${({ $active, theme }) => ($active ? '#FFF' : theme.colors.textAlt1)};
`

const StyledWrapIcon = styled(Box)<{ $active?: boolean }>`
  width: 24px;
  min-height: 28px;

  color: ${({ $active, theme }) => ($active ? '#FFF' : theme.colors.textAlt1)};
`

const StyledWrapTab = styled(Box).attrs({ disabledDefaultStyle: true })<{ $active?: boolean }>`
  height: ${({ theme }) => theme.bottombarHeight};
  padding: 8px 0;
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  justify-items: center;
  position: relative;
  width: 100%;
  cursor: pointer;
  align-items: center;

  @media screen and (min-width: 852px) {
    &:hover {
      * {
        color: ${({ theme }) => theme.colors.text};
      }
    }
  }
`

const StyledUnreadAmountDot = styled(Box)`
  width: 8px;
  height: 8px;

  border-radius: 50%;
  background: ${({ theme: { colors } }) => colors.strokeAlt3};
  position: absolute;
  top: -5px;
  right: -10px;
`
const StyledBonusCenterBadge = styled(RowCenter)<{ $isHaveNewBonus: boolean }>`
  background: ${({ theme: { colors } }) => colors.strokeAlt3};
  padding: 2px 4px;
  border-radius: 40px;

  min-width: 18px;
  min-height: 16px;
  font-size: 10px;
  font-weight: 600;

  position: absolute;
  right: 0;
  top: 0;
  transform: translate(60%, -40%);

  ${({ $isHaveNewBonus }) =>
    $isHaveNewBonus &&
    css`
      @keyframes ping {
        0% {
          transform: scale(1);
          opacity: 0.5;
        }
        100% {
          transform: scale(2);
          opacity: 0;
        }
      }

      &::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 40px;
        background: #fff;
        animation: ping 1.7s ease infinite;
        opacity: 0.25;
        z-index: 10;
      }
    `};
`

const StyledNotificationContainer = styled(Flex)`
  width: max-content;
  background: ${({ theme }) => theme.colors.textTertiary} !important;
  border-radius: ${({ theme }) => theme.radii.tiny};
  gap: 8px;
  padding: 4px 8px;
  z-index: 6;
  position: absolute;
  top: -40px;
  left: -200px;
  line-height: 20px;
  align-items: center;

  &:before {
    content: '';
    position: absolute;
    bottom: -5px;
    right: 124px;
    width: 10px;
    height: 10px;
    transform: rotate(135deg);
    border-radius: 1px;
    background: ${({ theme }) => theme.colors.textTertiary} !important;
    backdrop-filter: blur(6px);
    z-index: -2;
  }

  display: flex;

  ${({ theme }) => theme.mediaQueries.md} {
    display: none;
  }
`

const BottombarUserMenu: React.FC<BoxProps> = ({ ...props }) => {
  const { unreadAmount } = useUnreadNotificationAmount()
  const { avatar } = useUserInfo()
  const { isMobile } = useMatchBreakpoints()

  const { asPath } = useRouter()
  const renderContent = useCallback(() => {
    return (
      <StyledWrapTab mx="auto" {...props}>
        <StyledWrapIcon>
          <RowCenter position="relative">
            {avatar ? (
              <Avatar src={avatar} width={24} height={24} alt="user avatar" />
            ) : (
              <Skeleton height={28} width={24} />
            )}
            <StyledUnreadAmountDot display={unreadAmount > 0 ? 'block' : 'none'} />
          </RowCenter>
        </StyledWrapIcon>
        <StyledText textAlign="center" $active={asPath.includes('profile') || asPath === RouteConfig.Referral}>
          <Trans>Account</Trans>
        </StyledText>
      </StyledWrapTab>
    )
  }, [avatar, unreadAmount, asPath])

  return (
    <Box width="100%" id="bottombar-usermenu">
      {isMobile && <NextLinkFromReactRouter to={RouteConfig.MobileAccount}>{renderContent()}</NextLinkFromReactRouter>}
      {!isMobile && (
        <Dropdown dropdownContent={<UserMenuDropdown />} dropdownPosition="top-right">
          {renderContent()}
        </Dropdown>
      )}
    </Box>
  )
}

const NotificationBonus = () => {
  const [unwatchedNewBonus, onClose] = useUnwatchedNewBonusListener()
  const { isSigned } = useAuth()
  const closeMenu = useCloseMenu()

  return (
    unwatchedNewBonus &&
    isSigned && (
      <StyledNotificationContainer
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          closeMenu()
        }}
      >
        <Text color="#161C24 !important" fontSize="12px" fontWeight={500}>
          <Trans>You have received a new reward, check it out now!</Trans>
        </Text>
        <Flex
          alignItems="center"
          justifyContent="center"
          width="12px"
          height="12px"
          style={{ cursor: 'pointer' }}
          onClick={onClose}
        >
          <Icons.ClearIcon width="12px" height="12px" />
        </Flex>
      </StyledNotificationContainer>
    )
  )
}

const BottomBar: React.FC<BoxProps> = ({ ...props }) => {
  const { isMobile, isMd } = useMatchBreakpoints()

  const { hasSession } = useUserInfo()
  const { hideIconChat, visibleIconChat } = useIconChatHandle()
  const { toggleMenu, isMenuOpen } = useMenuContainer()

  const crisp = useCrisp()
  const elementContainerChatbox = useElementContainerChatboxRendered()
  const refToggleOpen = useRef(null)
  const { bonuses } = useUserBonus()
  const [unwatchedNewBonus, onClose] = useUnwatchedNewBonusListener()
  const closeMenu = useCloseMenu()

  const handleCheckCountMessage = () => {
    const isHaveMessage = crisp.get('chat:unread:count') > 0
    if (isHaveMessage) {
      visibleIconChat()
    } else {
      hideIconChat()
    }
  }

  useLayoutEffect(() => {
    if (elementContainerChatbox && crisp) {
      if (isMobile || isMd) {
        crisp.push([
          'on',
          'chat:closed',
          () => {
            hideIconChat()
            refToggleOpen.current = false
          },
        ])

        crisp.push([
          'on',
          'message:received',
          () => {
            handleCheckCountMessage()
          },
        ])

        handleCheckCountMessage()
      } else {
        try {
          crisp.push(['off', 'chat:closed'])
          crisp.push(['off', 'message:received'])
        } catch (e) {}

        visibleIconChat()
      }
    }
  }, [crisp, isMobile, isMd, elementContainerChatbox])

  const { asPath, prefetch } = useRouter()

  useEffect(() => {
    const prefetchRoutes = [RouteConfig.Home, RouteConfig.BonusCenter, RouteConfig.MobileAccount]

    prefetchRoutes
      .filter((route) => route !== asPath)
      .forEach((route) => {
        prefetch(route)
      })
  }, [asPath])

  const renderBottomTab = useMemo(() => {
    const menu = [
      {
        title: 'Menu',
        icon: 'MenuBarIcon',
        action: toggleMenu,
        id: 'menu',
        active: isMenuOpen,
      },
      {
        title: 'Home',
        icon: 'HomeIcon',
        route: RouteConfig.Home,
        id: 'home',
        active: asPath === RouteConfig.Home,
      },
    ]

    return menu.map((item) => {
      const Icon = getIcon(item.icon)

      const innerTab = (
        <>
          <StyledWrapIcon $active={item.active}>
            <Icon />
          </StyledWrapIcon>
          <StyledText $active={item.active}>
            <Trans>{item.title}</Trans>
          </StyledText>
        </>
      )

      return item.route ? (
        <StyledWrapTab
          id={item.id}
          as={Link}
          key={`bottom-bar-tab-${item.title}`}
          onClick={closeMenu}
          href={item.route}
        >
          {innerTab}
        </StyledWrapTab>
      ) : (
        <StyledWrapTab id={item.id} key={`bottom-bar-tab-${item.title}`} onClick={item.action}>
          {innerTab}
        </StyledWrapTab>
      )
    })
  }, [isMenuOpen, toggleMenu, asPath])

  return (
    <StyledWrapper id="bottombar" {...props}>
      {renderBottomTab}

      <StyledWrapTab
        id="bonus"
        as={Link}
        key={'bottom-bar-tab-Bonus'}
        href={RouteConfig.BonusCenter}
        onClick={() => {
          onClose()
          closeMenu()
        }}
      >
        <Box width="24px" position="relative">
          <NotificationBonus />
          <StyledWrapIcon $active={asPath === RouteConfig.BonusCenter}>
            <Icons.BonusIcon />
          </StyledWrapIcon>

          {!!bonuses?.length && (
            <StyledBonusCenterBadge $isHaveNewBonus={unwatchedNewBonus}>{bonuses.length}</StyledBonusCenterBadge>
          )}
        </Box>
        <StyledText $active={asPath === RouteConfig.BonusCenter}>
          <Trans>Bonus</Trans>
        </StyledText>
      </StyledWrapTab>

      {hasSession ? (
        <BottombarUserMenu />
      ) : (
        <StyledWrapTab onClick={closeMenu} id="bottom-login" as={Link} href={getActionUrlSearch(ActionQueryEnum.Login)}>
          <StyledWrapIcon>
            <Icons.LoginIcon />
          </StyledWrapIcon>
          <StyledText>
            <Trans>Log in</Trans>
          </StyledText>
        </StyledWrapTab>
      )}
    </StyledWrapper>
  )
}

export default React.memo(BottomBar)
